import type { Dispatch } from "@reduxjs/toolkit";
import { createAction, createReducer } from "@reduxjs/toolkit";
import type {
    EcoConsumptionSectionConfigEntryAceType,
    EcoConsumptionCostType,
    EcoConsumptionSectionType,
} from "../../types/EcoConsumptionType";
import type { CreateLogicType } from "../../../../../common-deprecated/redux/typeHelpers";
import type {
    FetchConfigurationSpecificationsBodyType,
    SortedSpecType,
} from "../../../../../common-deprecated/types/SpecTypes";
import type { CommonSettingsType } from "../../../../../common-deprecated/settings/fetchCommonSettings";
import type {
    AbortableRequestStatus,
    EngineType,
    FetchAndParseJsonType,
} from "../../../../../common-deprecated/types/CommonTypes";
import { RequestStatusEnum } from "../../../../../common-deprecated/types/CommonTypes";
import type { EcoConsumptionLabelsType } from "../../utils/labels";
import { ecoConsumptionLabels } from "../../utils/labels";
import type { CarMotorizationType, CarType } from "../../../../shared-logic/types/CarType";
import type { SpecStateType } from "../../../../shared-logic/redux/reducers/SpecsReducer";

export type EcoConsumptionStateType = {
    initialized: boolean;
    fetching: boolean;
    costsStatus: AbortableRequestStatus;
    configuration: EcoConsumptionSectionType | null;
    costs: EcoConsumptionCostType | null;
    labels: EcoConsumptionLabelsType;
    ace: EcoConsumptionSectionConfigEntryAceType[] | null;
};

export type EcoConsumptionReducerType = EcoConsumptionStateType;

export const ECO_CONSUMPTION_INIT = "ecoConsumption/initEcoConsumptionConfiguration";
export const ECO_CONSUMPTION_START_COSTS_FETCH = "ecoConsumption/startEcoConsumptionCostsFetch";
export const ECO_CONSUMPTION_SET_COSTS = "ecoConsumption/setEcoConsumptionCosts";
export const ECO_CONSUMPTION_SET_CONFIGURATION = "ecoConsumption/setEcoConsumptionConfiguration";
export const ECO_CONSUMPTION_RESET_COSTS = "ecoConsumption/resetEcoConsumptionCosts";

export const initialStateEcoConsumptionReducer: EcoConsumptionReducerType = {
    initialized: false,
    fetching: false,
    costsStatus: RequestStatusEnum.Idle,
    configuration: null,
    costs: null,
    ace: null,
    labels: ecoConsumptionLabels,
};

export type InitEcoConsumptionConfigurationType = {
    ace: EcoConsumptionSectionConfigEntryAceType[] | null;
    labels: EcoConsumptionLabelsType | null;
};

export const initEcoConsumptionConfiguration = createAction<
    InitEcoConsumptionConfigurationType,
    typeof ECO_CONSUMPTION_INIT
>(ECO_CONSUMPTION_INIT);

type StartEcoConsumptionCostsFetchType = {
    config: FetchConfigurationSpecificationsBodyType;
    commonSettings: CommonSettingsType;
    engine: EngineType | CarMotorizationType["engine"];
    techSpecs: SortedSpecType[];
    ecoConsumptionCostService: string;
    fetchAndParseJson?: FetchAndParseJsonType;
};

export const startEcoConsumptionCostsFetch = createAction<
    StartEcoConsumptionCostsFetchType,
    typeof ECO_CONSUMPTION_START_COSTS_FETCH
>(ECO_CONSUMPTION_START_COSTS_FETCH);

type SetEcoConsumptionCostsType = {
    costs: EcoConsumptionCostType | null;
};

export const setEcoConsumptionCosts = createAction<SetEcoConsumptionCostsType, typeof ECO_CONSUMPTION_SET_COSTS>(
    ECO_CONSUMPTION_SET_COSTS,
);

type SetEcoConsumptionType = {
    configuration: EcoConsumptionSectionType;
};

type ResetEcoConsumptionCosts = EcoConsumptionStateType["costsStatus"];

export const setEcoConsumptionConfiguration = createAction<
    SetEcoConsumptionType,
    typeof ECO_CONSUMPTION_SET_CONFIGURATION
>(ECO_CONSUMPTION_SET_CONFIGURATION);

export const resetEcoConsumptionCosts = createAction<ResetEcoConsumptionCosts, typeof ECO_CONSUMPTION_RESET_COSTS>(
    ECO_CONSUMPTION_RESET_COSTS,
);

export type EcoConsumptionActionsType =
    | ReturnType<typeof initEcoConsumptionConfiguration>
    | ReturnType<typeof startEcoConsumptionCostsFetch>
    | ReturnType<typeof setEcoConsumptionCosts>
    | ReturnType<typeof setEcoConsumptionConfiguration>
    | ReturnType<typeof resetEcoConsumptionCosts>;

export type EcoConsumptionDispatchType = Dispatch<EcoConsumptionActionsType>;

export type EcoConsumptionLogicType<ActionsType extends EcoConsumptionActionsType> = CreateLogicType<
    ActionsType,
    { ecoConsumption: EcoConsumptionStateType; commonSettings: CommonSettingsType; specs: SpecStateType; car: CarType },
    EcoConsumptionDispatchType
>;

const EcoConsumptionReducer = createReducer(initialStateEcoConsumptionReducer, (builder) => {
    builder.addCase(initEcoConsumptionConfiguration, (state, action) => {
        const { labels, ace } = action.payload;
        return {
            ...state,
            initialized: true,
            ace,
            labels: {
                ...state.labels,
                ...labels,
            },
        };
    });
    builder.addCase(startEcoConsumptionCostsFetch, (state) => {
        return {
            ...state,
            fetching: true,
            costsStatus: RequestStatusEnum.Fetching,
        };
    });
    builder.addCase(setEcoConsumptionCosts, (state, action) => {
        const { costs } = action.payload;
        return {
            ...state,
            fetching: false,
            costsStatus: RequestStatusEnum.Complete,
            costs,
        };
    });
    builder.addCase(setEcoConsumptionConfiguration, (state, action) => {
        const { configuration } = action.payload;
        return {
            ...state,
            configuration,
        };
    });
    builder.addCase(resetEcoConsumptionCosts, (state, action) => {
        return {
            ...state,
            fetching: false,
            costsStatus: action.payload,
            costs: null,
        };
    });
});

export default EcoConsumptionReducer;
